import {
  Warning,
  CheckCircle,
  Clock,
  FileText,
  Gauge,
  Invoice,
  ArrowCounterClockwise,
  Sparkle,
  Tag,
  TrendDown,
  XCircle,
} from '@phosphor-icons/react';
import { PageHeader } from '@/components/layout/PageHeader';
import { EmptyState } from '@/components/layout/EmptyState';
import { MonthlyStatement } from '@/components/billing/MonthlyStatement';
import { useBilling, type NeuraxInvoice } from '@/lib/billing/useBilling';
import {
  applyDiscount,
  computeActiveTier,
  usePricing,
  type VolumeTier,
  type NeuraxPricing,
} from '@/lib/pricing/usePricing';
import { useRefunds, type NeuraxRefund } from '@/lib/refunds/useRefunds';
import { cn } from '@/lib/utils/cn';

// Companies without an active b2b_pricing_configs row bill at the standard
// rates — mirror the get_b2b_billing_statement fallback exactly so the
// pricing card never disagrees with the statement.
const DEFAULT_PRICING: NeuraxPricing = {
  per_session_fee: 14.99,
  interview_price: 14.99,
  assessment_price: 7.99,
  token_threshold: 0,
  overage_per_1k_tokens: 0,
  currency: 'USD',
  volume_tiers: [],
};

export default function BillingPage() {
  const { data, isLoading, isError, error } = useBilling();
  const { data: pricing } = usePricing();
  const { data: refunds } = useRefunds();

  return (
    <>
      <PageHeader
        eyebrow="Billing"
        title="Plan & statements"
        description="Track NeuraX usage against your plan and review itemized monthly statements for any billing period."
      />

      {isError && (
        <div
          role="alert"
          className="mb-6 flex items-start gap-3 rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive"
        >
          <Warning className="mt-0.5 h-4 w-4" />
          <span>{(error as Error)?.message || 'Failed to load billing.'}</span>
        </div>
      )}

      {isLoading && (
        <div className="space-y-4">
          <div data-testid="billing-skeleton" className="glass-card h-40 animate-pulse" />
          <div data-testid="billing-skeleton" className="glass-card h-24 animate-pulse" />
          <div data-testid="billing-skeleton" className="glass-card h-24 animate-pulse" />
        </div>
      )}

      {!isLoading && data && (
        <div className="space-y-6">
          <PlanCard tier={data.tier} quota={data.quota} />
          <PricingCard pricing={pricing ?? DEFAULT_PRICING} usedSetups={data.quota.used} />
          <MonthlyStatement />
          <RefundsSection refunds={refunds ?? []} />
          <InvoicesSection invoices={data.invoices} />
        </div>
      )}
    </>
  );
}

function PricingCard({ pricing, usedSetups }: { pricing: NeuraxPricing; usedSetups: number }) {
  const activeTier = computeActiveTier(pricing.volume_tiers ?? [], usedSetups);
  const effectiveInterview = applyDiscount(pricing.interview_price, activeTier);
  const effectiveAssessment = applyDiscount(pricing.assessment_price, activeTier);

  return (
    <div data-testid="pricing-card" className="glass-card relative overflow-hidden p-6">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-neurax-amber/40 via-neurax-electric/40 to-transparent" />

      <div className="flex flex-wrap items-start justify-between gap-6">
        <div>
          <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-neurax-amber/80">
            Pricing
          </p>
          <h2 className="mt-2 font-display text-2xl font-semibold text-white">Per-setup rates</h2>
          <p className="mt-1.5 max-w-md text-sm text-muted-foreground">
            Billed per confirmed interview or assessment setup — one charge per new prompt
            generation. Regenerating a prompt during setup is never charged. Volume discounts apply
            automatically once your monthly billable setup count crosses each threshold.
          </p>
        </div>
        <div className="grid h-12 w-12 place-items-center rounded-2xl bg-white/[0.04] text-neurax-amber">
          <Tag className="h-5 w-5" />
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <PriceTile
          testId="pricing-interview"
          label="Interview"
          base={pricing.interview_price}
          effective={effectiveInterview}
          currency={pricing.currency}
          tier={activeTier}
        />
        <PriceTile
          testId="pricing-assessment"
          label="Assessment"
          base={pricing.assessment_price}
          effective={effectiveAssessment}
          currency={pricing.currency}
          tier={activeTier}
        />
      </div>

      <div className="mt-6 overflow-hidden rounded-xl border border-white/5">
        <div className="grid grid-cols-12 gap-4 border-b border-white/5 bg-white/[0.02] px-4 py-2.5 text-[11px] uppercase tracking-wider text-muted-foreground">
          <div className="col-span-5 sm:col-span-4">Volume tier</div>
          <div className="col-span-3">Discount</div>
          <div className="col-span-4 sm:col-span-3">Interview after discount</div>
          <div className="hidden sm:col-span-2 sm:block text-right">Status</div>
        </div>
        <div className="divide-y divide-white/5">
          {(pricing.volume_tiers ?? []).map((t) => (
            <TierRow
              key={t.min_orders}
              tier={t}
              base={pricing.interview_price}
              currency={pricing.currency}
              isActive={activeTier?.min_orders === t.min_orders}
              isReached={usedSetups >= t.min_orders}
              progress={Math.min(100, Math.round((usedSetups / t.min_orders) * 100))}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function PriceTile({
  testId,
  label,
  base,
  effective,
  currency,
  tier,
}: {
  testId: string;
  label: string;
  base: number;
  effective: number;
  currency: string;
  tier: VolumeTier | null;
}) {
  const discounted = tier && effective < base;
  return (
    <div data-testid={testId} className="rounded-xl border border-white/5 bg-white/[0.02] p-4">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <div className="mt-1 flex items-baseline gap-2">
        <p className="font-display text-xl font-semibold text-white">
          {fmtAmount(effective, currency, true)}
        </p>
        {discounted && (
          <span className="text-xs text-muted-foreground line-through">
            {fmtAmount(base, currency, true)}
          </span>
        )}
      </div>
      {discounted && (
        <p className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-neurax-mint">
          <TrendDown className="h-3 w-3" />
          {tier!.discount_pct}% volume discount applied
        </p>
      )}
      {!discounted && (
        <p className="mt-1 text-[11px] text-muted-foreground">Standard rate per setup</p>
      )}
    </div>
  );
}

function TierRow({
  tier,
  base,
  currency,
  isActive,
  isReached,
  progress,
}: {
  tier: VolumeTier;
  base: number;
  currency: string;
  isActive: boolean;
  isReached: boolean;
  progress: number;
}) {
  const effective = applyDiscount(base, tier);
  return (
    <div
      data-testid={`tier-row-${tier.min_orders}`}
      data-active={isActive}
      className={cn(
        'grid grid-cols-12 items-center gap-4 px-4 py-3 transition',
        isActive && 'bg-neurax-mint/[0.06]'
      )}
    >
      <div className="col-span-5 sm:col-span-4">
        <p className="text-sm font-medium text-white">{tier.min_orders}+ setups / mo</p>
        <div className="mt-1 h-1 overflow-hidden rounded-full bg-white/5">
          <div
            className={cn(
              'h-full rounded-full bg-gradient-to-r transition-all',
              isReached ? 'from-neurax-mint to-neurax-electric' : 'from-white/20 to-white/10'
            )}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
      <div className="col-span-3 text-sm font-semibold text-neurax-amber">
        −{tier.discount_pct}%
      </div>
      <div className="col-span-4 sm:col-span-3 font-display text-sm text-white">
        {fmtAmount(effective, currency, true)}
      </div>
      <div className="hidden sm:col-span-2 sm:flex sm:justify-end">
        {isActive ? (
          <span className="inline-flex items-center gap-1 rounded-full border border-neurax-mint/30 bg-neurax-mint/10 px-2.5 py-1 text-[11px] font-medium uppercase tracking-wider text-neurax-mint">
            <CheckCircle className="h-3 w-3" />
            Active
          </span>
        ) : isReached ? (
          <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
            Surpassed
          </span>
        ) : (
          <span className="text-[11px] uppercase tracking-wider text-muted-foreground">Locked</span>
        )}
      </div>
    </div>
  );
}

function RefundsSection({ refunds }: { refunds: NeuraxRefund[] }) {
  return (
    <div className="glass-panel overflow-hidden">
      <div className="flex items-center justify-between border-b border-white/5 px-6 py-4">
        <div className="flex items-center gap-2">
          <ArrowCounterClockwise className="h-4 w-4 text-neurax-electric" />
          <h3 className="text-sm font-semibold text-white">Refund requests</h3>
        </div>
        <p className="text-xs text-muted-foreground">{refunds.length} total</p>
      </div>

      {refunds.length === 0 ? (
        <div data-testid="refunds-empty" className="px-6 py-10">
          <EmptyState
            title="No refund requests"
            description="Open a refund from any interview session if something didn't go as planned."
          />
        </div>
      ) : (
        <div className="divide-y divide-white/5">
          {refunds.map((r) => (
            <RefundRow key={r.id} r={r} />
          ))}
        </div>
      )}
    </div>
  );
}

function RefundRow({ r }: { r: NeuraxRefund }) {
  return (
    <div
      data-testid={`refund-row-${r.id}`}
      className="grid grid-cols-12 items-start gap-4 px-6 py-4"
    >
      <div className="col-span-7 sm:col-span-6 min-w-0">
        <p className="text-sm font-medium text-white capitalize">{r.reason.replace(/_/g, ' ')}</p>
        {r.reason_details && (
          <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">{r.reason_details}</p>
        )}
        <p className="mt-1 text-[11px] text-muted-foreground">
          Filed{' '}
          {new Date(r.created_at).toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}
          {r.requested_by_name ? ` by ${r.requested_by_name}` : ''}
          {r.session_type
            ? ` · ${r.session_type === 'online_assessment' ? 'assessment' : r.session_type}`
            : ''}
        </p>
      </div>
      <div className="col-span-2 hidden font-display text-sm text-white sm:block">
        {r.session_amount != null ? fmtAmount(r.session_amount, 'USD') : '—'}
      </div>
      <div className="col-span-5 sm:col-span-4 flex justify-end">
        <RefundStatusBadge status={r.status} />
      </div>
    </div>
  );
}

function RefundStatusBadge({ status }: { status: string }) {
  const s = status.toLowerCase();
  if (s === 'approved' || s === 'processed' || s === 'auto_approved') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-neurax-mint/30 bg-neurax-mint/10 px-2.5 py-1 text-[11px] font-medium uppercase tracking-wider text-neurax-mint">
        <CheckCircle className="h-3 w-3" />
        {s === 'processed' ? 'Processed' : 'Approved'}
      </span>
    );
  }
  if (s === 'rejected') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-destructive/30 bg-destructive/10 px-2.5 py-1 text-[11px] font-medium uppercase tracking-wider text-destructive">
        <XCircle className="h-3 w-3" />
        Rejected
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-neurax-amber/30 bg-neurax-amber/10 px-2.5 py-1 text-[11px] font-medium uppercase tracking-wider text-neurax-amber">
      <Clock className="h-3 w-3" />
      Pending
    </span>
  );
}

function PlanCard({
  tier,
  quota,
}: {
  tier: string;
  quota: { used: number; total: number; percent: number };
}) {
  const remaining = Math.max(0, quota.total - quota.used);
  const overUsage = quota.percent >= 90;

  return (
    <div className="glass-card relative overflow-hidden p-6">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-neurax-electric/40 via-neurax-plasma/40 to-transparent" />
      <div className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-neurax-electric/10 blur-3xl" />

      <div className="relative flex flex-wrap items-start justify-between gap-6">
        <div>
          <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-neurax-electric/80">
            Current plan
          </p>
          <div className="mt-2 flex items-center gap-3">
            <h2
              data-testid="billing-tier"
              className="font-display text-3xl font-semibold capitalize text-white"
            >
              {tier}
            </h2>
            <span className="inline-flex items-center gap-1 rounded-full border border-neurax-amber/30 bg-neurax-amber/10 px-2.5 py-1 text-[11px] font-medium uppercase tracking-wider text-neurax-amber">
              <Sparkle className="h-3 w-3" />
              Active
            </span>
          </div>
          <p className="mt-2 max-w-md text-sm text-muted-foreground">
            Includes {quota.total} interview setups per month with priority NeuraX prompt
            generation.
          </p>
        </div>

        <div className="grid h-12 w-12 place-items-center rounded-2xl bg-white/[0.04] text-neurax-mint">
          <Gauge className="h-5 w-5" />
        </div>
      </div>

      <div className="relative mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Metric
          label="Billable setups"
          value={
            <span data-testid="billing-quota">
              <span className="text-white">{quota.used}</span>
              <span className="ml-1 text-base font-medium text-muted-foreground">
                / {quota.total}
              </span>
            </span>
          }
        />
        <Metric label="Remaining" value={<span className="text-white">{remaining}</span>} />
        <Metric
          label="Cycle utilization"
          value={
            <span
              data-testid="billing-quota-percent"
              className={cn(overUsage ? 'text-neurax-amber' : 'text-white')}
            >
              {quota.percent}%
            </span>
          }
        />
      </div>

      <div className="relative mt-5 h-2 overflow-hidden rounded-full bg-white/5">
        <div
          className={cn(
            'h-full rounded-full bg-gradient-to-r transition-all',
            overUsage ? 'from-neurax-amber to-destructive' : 'from-neurax-mint to-neurax-electric'
          )}
          style={{ width: `${Math.min(100, quota.percent)}%` }}
        />
      </div>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-white/5 bg-white/[0.02] p-3.5">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="mt-1 font-display text-2xl font-semibold tracking-tight">{value}</p>
    </div>
  );
}

function InvoicesSection({ invoices }: { invoices: NeuraxInvoice[] }) {
  if (invoices.length === 0) {
    return (
      <div data-testid="invoices-empty">
        <EmptyState
          title="No invoices yet"
          description="Charges are computed live in the monthly statement above. Formal invoices, when issued, will appear here."
        />
      </div>
    );
  }

  return (
    <div className="glass-panel overflow-hidden">
      <div className="flex items-center justify-between border-b border-white/5 px-6 py-4">
        <div className="flex items-center gap-2">
          <Invoice className="h-4 w-4 text-neurax-electric" />
          <h3 className="text-sm font-semibold text-white">Invoices</h3>
        </div>
        <p className="text-xs text-muted-foreground">{invoices.length} total</p>
      </div>

      <div className="grid grid-cols-12 gap-4 border-b border-white/5 px-6 py-3 text-[11px] uppercase tracking-wider text-muted-foreground">
        <div className="col-span-4 sm:col-span-3">Invoice</div>
        <div className="col-span-4 sm:col-span-3">Period</div>
        <div className="hidden sm:col-span-2 sm:block">Sessions</div>
        <div className="col-span-4 sm:col-span-2 text-right">Amount</div>
        <div className="hidden sm:col-span-2 sm:block text-right">Status</div>
      </div>

      <div className="divide-y divide-white/5">
        {invoices.map((inv) => (
          <InvoiceRow key={inv.id} inv={inv} />
        ))}
      </div>
    </div>
  );
}

function InvoiceRow({ inv }: { inv: NeuraxInvoice }) {
  return (
    <div
      data-testid={`invoice-row-${inv.id}`}
      className="grid grid-cols-12 items-center gap-4 px-6 py-4"
    >
      <div className="col-span-4 sm:col-span-3 flex items-center gap-2 min-w-0">
        <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="truncate text-sm font-medium text-white">{inv.invoice_number}</span>
      </div>
      <div className="col-span-4 sm:col-span-3 text-xs text-muted-foreground">
        {fmtDate(inv.period_start)} → {fmtDate(inv.period_end)}
      </div>
      <div className="hidden sm:col-span-2 sm:block text-sm text-white">
        {inv.total_sessions ?? 0}
      </div>
      <div className="col-span-4 sm:col-span-2 text-right font-display text-sm font-semibold text-white">
        {fmtAmount(inv.total_amount, inv.currency)}
      </div>
      <div className="hidden sm:col-span-2 sm:flex sm:justify-end">
        <InvoiceStatus status={inv.status} />
      </div>
    </div>
  );
}

function InvoiceStatus({ status }: { status: string }) {
  const s = status.toLowerCase();
  if (s === 'paid') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-neurax-mint/30 bg-neurax-mint/10 px-2.5 py-1 text-[11px] font-medium uppercase tracking-wider text-neurax-mint">
        <CheckCircle className="h-3 w-3" />
        Paid
      </span>
    );
  }
  if (s === 'overdue') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-destructive/30 bg-destructive/10 px-2.5 py-1 text-[11px] font-medium uppercase tracking-wider text-destructive">
        <Warning className="h-3 w-3" />
        Overdue
      </span>
    );
  }
  if (s === 'sent') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-neurax-electric/30 bg-neurax-electric/10 px-2.5 py-1 text-[11px] font-medium uppercase tracking-wider text-neurax-electric">
        <Clock className="h-3 w-3" />
        Sent
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
      {s.charAt(0).toUpperCase() + s.slice(1)}
    </span>
  );
}

function fmtDate(s: string) {
  return new Date(s).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function fmtAmount(n: number, ccy: string, showCents = false) {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: ccy || 'USD',
      minimumFractionDigits: showCents ? 2 : 0,
      maximumFractionDigits: showCents ? 2 : 0,
    }).format(n);
  } catch {
    return showCents ? `$${n.toFixed(2)}` : `$${Math.round(n)}`;
  }
}
